import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { createColumnHelper } from '@tanstack/react-table'
import { Campaign } from '../../../../lib/types/Campaign'
import { Link, Outlet, useNavigate, useSearchParams } from 'react-router-dom'
import { lowerCase } from 'lodash'
import { useGetAllCampaignsByDealership } from '../../../../api/dealership/campaigns/useGetAllCampaignsByDealership'
import {
  AsyncStatsCard,
  Button,
  CardRadioInput,
  DateRangePicker,
  Pill,
  Table,
} from '@demandlocal/components'
import CampaignOverviewCard from '../../../../components/Campaigns/CampaignOverviewCard'
import { DateRange } from '../../../../lib/types/DateRange'
import dayjs from 'dayjs'
import { useCampaignsListView } from '../../../../store/zustand/campaignsListStore'
import { ReactComponent as IconChangeViewCard } from '../../../../images/icons/icon_change_view_card.svg'
import { ReactComponent as IconChangeViewList } from '../../../../images/icons/icon_change_view_list.svg'
import { useDateRange } from '../../../../store/zustand/dateRangeStore'
import { formatCurrency, formatNumber } from '../../../../lib/utils/formatNumber'
import { useDealership } from '../../../../store/zustand/dealershipStore'
import { useGetAgencyByAgencyId } from '../../../../api/agencies/useGetAgencyByAgencyId'
import { ReactComponent as IconImport } from '../../../../images/icons/icon_import_large.svg'
import { ReactComponent as IconCreate } from '../../../../images/icons/icon_create_large.svg'
import { ReactComponent as IconGoogleAds } from '../../../../images/vendors/google_ads_logo.svg'
import { ReactComponent as IconFacebookAds } from '../../../../images/vendors/meta_logo.svg'
import { FieldValues, FormProvider, useForm } from 'react-hook-form'
import { motion } from 'framer-motion'

const columnHelper = createColumnHelper<Campaign>()

const CampaignsList = (): JSX.Element => {
  const dealership = useDealership()
  const { view, setView } = useCampaignsListView()
  const { dateRange, setDateRange } = useDateRange()
  const [queryParams, setQueryParams] = useSearchParams()
  const { data: agency } = useGetAgencyByAgencyId(dealership?.organization_id)
  const [filters, setFilters] = useState<DateRange>({
    dateRange: {
      startDate: queryParams.get('startDate') || dateRange.dateRange.startDate,
      endDate: queryParams.get('endDate') || dateRange.dateRange.endDate,
    },
  })

  const { data: campaigns, isLoading: isLoadingCampaigns } =
    useGetAllCampaignsByDealership({
      filters: {
        ...filters.dateRange,
        includeAdditionalMetrics: 0,
      },
    })

  const { data: activeCampaigns, isLoading: isLoadingActiveCampaigns } =
    useGetAllCampaignsByDealership({
      filters: {
        startDate: dayjs().startOf('month').format('YYYY-MM-DD'),
        endDate: dayjs().startOf('day').format('YYYY-MM-DD'),
        includeAdditionalMetrics: 0,
      },
    })

  const hasCampaigns = useMemo(
    () => (campaigns?.data?.length ?? 0) > 0,
    [campaigns?.data],
  )

  const onDateRangeChange = (dateRange: DateRange): void => {
    setFilters((prev: DateRange) => ({
      ...prev,
      ...dateRange,
    }))
    setDateRange(dateRange)
  }

  useEffect(() => {
    setQueryParams(filters.dateRange)
  }, [filters.dateRange, setQueryParams])

  const campaignColumns = useMemo(() => {
    const baseColumns = [
      columnHelper.accessor('campaign_status', {
        header: () => 'Status',
        cell: (info) => {
          return (
            <Pill type={info.getValue() === 'active' ? 'success' : 'warning'}>
              {info.getValue()}
            </Pill>
          )
        },
      }),
      ...(Boolean(agency?.show_budget_type)
        ? [
            columnHelper.display({
              header: 'Budget',
              id: 'budget',
              cell: ({ row }) =>
                agency?.show_budget_type === 1
                  ? formatCurrency(row.original.demand_local_net)
                  : formatCurrency(row.original.budget),
            }),
          ]
        : []),
      columnHelper.accessor('start_date', {
        header: () => 'Booking Month',
        cell: (info) => dayjs(info.getValue()).format("MMM 'YY"),
      }),
      columnHelper.accessor('client_campaign_display_name', {
        header: () => 'Name',
        cell: (info) =>
          info.row.original.campaign_status === 'pending' ? (
            <p className={'text-primary-persian-blue font-bold'}>
              {info.getValue() ?? '-'}
            </p>
          ) : (
            <Link
              className={'text-primary-persian-blue font-bold'}
              to={`../${lowerCase(info.row.original.product_code)}/${
                info.row.original.client_campaign_id
              }/?startDate=${dayjs(info.row.original.start_date).format(
                'YYYY-MM-DD',
              )}&endDate=${dayjs(info.row.original.end_date).format('YYYY-MM-DD')}`}
              onContextMenu={() => {
                window.open(
                  `./${lowerCase(info.row.original.product_code)}/${
                    info.row.original.client_campaign_id
                  }/?startDate=${dayjs(info.row.original.start_date).format(
                    'YYYY-MM-DD',
                  )}&endDate=${dayjs(info.row.original.end_date).format('YYYY-MM-DD')}`,
                  '_blank',
                )
              }}>
              {info.getValue() || '-'}
            </Link>
          ),
      }),
      columnHelper.display({
        header: 'Product',
        id: 'product',
        cell: ({ row }) =>
          row.original.campaign_type === 'audience'
            ? `${row.original.product_name}`
            : `${row.original.product_name} (${row.original.product_code})`,
      }),
      columnHelper.accessor('impressions', {
        header: () => 'Impressions',
        cell: (info) =>
          formatNumber(
            (info.row.original.product_code === 'DOH'
              ? info.row.original.audience_impressions
              : info.getValue()) || 0,
          ),
      }),
      columnHelper.accessor('clicks', {
        header: () => 'Clicks',
        cell: (info) =>
          info.row.original.product_code === 'DOH'
            ? 'N/A'
            : formatNumber(info.getValue() || 0),
      }),
      columnHelper.display({
        header: () => 'CTR',
        id: 'ctr',
        cell: ({ row }) =>
          row.original?.clicks && row.original?.impressions
            ? `${
                Math.round((row.original?.clicks / row.original?.impressions) * 10000) /
                100
              }%`
            : 'N/A',
      }),
    ]
    return baseColumns
  }, [agency?.show_budget_type])

  const activeCampaignsCount = useMemo(
    () =>
      activeCampaigns?.data?.filter((c) => c.campaign_status === 'active').length ?? 0,
    [activeCampaigns?.data],
  )

  const clicksCount = useMemo(
    () =>
      activeCampaigns?.data
        ?.filter((c) => c.campaign_status === 'active')
        .reduce((acc, cur) => acc + (Number(cur?.clicks) ?? 0), 0),
    [activeCampaigns?.data],
  )

  const impressionsCount = useMemo(
    () =>
      activeCampaigns?.data
        ?.filter((c) => c.campaign_status === 'active')
        .reduce((acc, cur) => acc + (Number(cur?.impressions) ?? 0), 0),
    [activeCampaigns?.data],
  )

  const navigate = useNavigate()
  const formContext = useForm<FieldValues>()
  const {
    handleSubmit,
    formState: { isDirty },
  } = formContext

  const onSubmit = useCallback(
    (values: Partial<FieldValues>): void => {
      const { create_campaign_type } = values
      if (create_campaign_type) navigate(`./create/${create_campaign_type}`)
    },
    [navigate],
  )

  const renderForm = useMemo(() => {
    return (
      <FormProvider {...formContext}>
        <motion.form onSubmit={handleSubmit(onSubmit)}>
          <div className="felx flex-col justify-center items-center">
            <div className={'grid grid-cols-2 gap-5 h-fit px-12'}>
              <CardRadioInput
                icon={<IconCreate />}
                name={'create_campaign_type'}
                value={'new'}
                label={'Create Campaigns'}
                description={'Create a campaign from scratch'}
                background={'white'}
              />
              <CardRadioInput
                icon={<IconImport />}
                name={'create_campaign_type'}
                value={'import'}
                label={'Import Campaigns'}
                description={
                  <div>
                    <h3>
                      Import campaigns from your Google Ads, Facebook Ads, or other
                      sources
                    </h3>
                    <div
                      className={'flex flex-row items-center justify-center gap-2 mt-4'}>
                      <IconGoogleAds className={'w-8 h-8'} />
                      <IconFacebookAds className={'w-8 h-8'} />
                    </div>
                  </div>
                }
                background={'white'}
              />
            </div>
            <div className="flex flex-row justify-center pt-5">
              <Button variant={'primary'} type="submit" disabled={!isDirty}>
                Continue
              </Button>
            </div>
          </div>
        </motion.form>
      </FormProvider>
    )
  }, [formContext, handleSubmit, isDirty, onSubmit])

  return (
    <div>
      <div className={'flex flex-col gap-12 pb-8'}>
        <div className={'flex flex-col gap-4'}>
          <span className="text-md pb-5">
            Track campaigns managed by LinkOne Media with detailed performance metrics.
          </span>
          {!isLoadingActiveCampaigns && activeCampaignsCount > 0 && (
            <div className="flex flex-row gap-3">
              <AsyncStatsCard
                title="Active Campaigns"
                content={formatNumber(activeCampaignsCount)}
                color="bg-primary-purple/20"
                isLoading={isLoadingActiveCampaigns}
              />
              <AsyncStatsCard
                title="Impressions from Active Campaigns"
                content={formatNumber(impressionsCount)}
                color="bg-light-cyan"
                isLoading={isLoadingActiveCampaigns}
              />
              <AsyncStatsCard
                title="Clicks from Active Campaigns"
                content={formatNumber(clicksCount)}
                color="bg-champagne-pink"
                isLoading={isLoadingActiveCampaigns}
              />
            </div>
          )}
          <div className="flex flex-row justify-between items-center">
            <span className={'font-bold text-primary-xiketic text-sm'}>
              Managed Campaigns
            </span>
            <div className={'flex flex-row gap-2'}>
              <DateRangePicker filters={filters} onChange={onDateRangeChange} />
              <div className={'flex items-center gap-3'}>
                {view === 'grid' && (
                  <div onClick={() => setView(view === 'grid' ? 'table' : 'grid')}>
                    <IconChangeViewList
                      className={
                        'cursor-pointer stroke-primary-xiketic fill-primary-xiketic'
                      }
                    />
                  </div>
                )}
                {view === 'table' && (
                  <div onClick={() => setView(view === 'table' ? 'grid' : 'table')}>
                    <IconChangeViewCard
                      className={'cursor-pointer stroke-primary-xiketic'}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
          {view === 'table' && (
            <div>
              {!isLoadingCampaigns && !hasCampaigns && renderForm}
              {!isLoadingCampaigns && hasCampaigns && (
                <Table
                  hidePagination={true}
                  emptyLabel={'No campaigns found'}
                  columns={campaignColumns}
                  filters={{ ...filters.dateRange }}
                  fetchFn={useGetAllCampaignsByDealership}
                />
              )}
            </div>
          )}
          {view === 'grid' && (
            <div>
              {isLoadingCampaigns && <div>Loading ...</div>}
              {!isLoadingCampaigns && !hasCampaigns && renderForm}
              {!isLoadingCampaigns && hasCampaigns && (
                <div className="grid grid-cols-3 gap-5">
                  {campaigns?.data.map((campaign) => {
                    return (
                      <CampaignOverviewCard.Managed
                        key={`${campaign.client_campaign_id}-${campaign.start_date}`}
                        item={campaign}
                        show_budget_type={agency?.show_budget_type || 0}
                      />
                    )
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <Outlet />
    </div>
  )
}

export default CampaignsList
